<template>
    <div>
        <div class="bg-gray2 py-1">
            <div class="container-fluid">
                <div class="d-flex justify-content-between align-items-end">
                    <!--/* Zone Fil d'ariane */-->
                    <ob1-breadcrumb :items="breadcrumbItems" style="min-width: 0" class="me-2 breadcrumb-full" />
                    <div v-if="dto.digitalCartTooltipDto">
                        <div class="row justify-content-end">
                            <div class="col-auto">
                                <digital-cart-tooltip
                                    :title="dto.digitalCartTooltipDto.title"
                                    :content="dto.digitalCartTooltipDto.content"
                                    :cta-label="dto.digitalCartTooltipDto.ctaLabel"
                                    :cta-url="dto.digitalCartTooltipDto.cartPageUrl"
                                    :empty-cart="dto.digitalCartTooltipDto.emptyCart"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <!--/* Capsule offre */-->
                <selected-offer
                    v-if="dto.selectedOffer && !dto.selectedOffer.fromCart"
                    class="mt-2 mb-1 d-webview-none"
                    :title="dto.selectedOffer.title"
                    :continue-without-mobile="dto.selectedOffer.continueWithoutMobile"
                    :modify-mobile-label="dto.selectedOffer.modifyMobileLabel"
                    :modify-offer="dto.selectedOffer.modifyOffer"
                    :offer-label="dto.selectedOffer.offerLabel"
                />
                <div class="d-webview-none">
                    <contract-phone-lines
                        v-if="dto.contractInfos"
                        class="mb-md-1 my-lg-1"
                        :contract-infos="dto.contractInfos"
                    />
                </div>
                <incentive
                    v-if="dto.incentiveDto"
                    :title="dto.incentiveDto.question"
                    :text="dto.incentiveDto.message"
                    :link-href="dto.changerMobileLink"
                    :link-label="dto.incentiveDto.linkLabel"
                />
                <intention-switch
                    v-if="dto.intentionSwitchDto"
                    :shop-sosh="isBrandSosh"
                    :title="dto.intentionSwitchDto.title"
                    :acquisition-label="currentVersion.firstParty ? dto.intentionSwitchDto.acquisitionLabel : null"
                    :tseul-label="dto.intentionSwitchDto.tseulLabel"
                    :changer-de-mobile-label="dto.intentionSwitchDto.changerDeMobileLabel"
                    :default-intention-checked="intentionParam"
                    :linked-with-results="true"
                    @switch-intention="switchIntention($event)"
                />
                <template v-if="dto.customerPopin">
                    <customer-popin
                        id="customer-popin-id"
                        :title="dto.customerPopin.customerPopinLabelsDto.title"
                        :subtitle="dto.customerPopin.customerPopinLabelsDto.subtitle"
                        :text="dto.customerPopin.customerPopinLabelsDto.text"
                        :button-label="dto.customerPopin.customerPopinLabelsDto.buttonLabel"
                        :button-link="dto.customerPopin.customerPopinLinksDto.buttonLink"
                        :link-label-html="dto.customerPopin.customerPopinLabelsDto.linkLabelHtml"
                        :link="dto.customerPopin.customerPopinLinksDto.link"
                        :intention="dto.customerPopin.intention"
                    />
                </template>
                <div class="d-flex flex-column flex-md-row mb-1 header">
                    <!--/* banner - brand et le nom du du mobile  */-->
                    <h1 class="mb-0 me-md-1">
                        <span
                            v-if="bannerAndBrand"
                            class="h3 fw-normal"
                        >
                            {{ bannerAndBrand }}</span>
                        <br>
                        <strong>{{ currentVersion.name }}</strong>
                    </h1>

                    <!--/* rating-summary */-->
                    <Rating
                        v-if="displayRatings"
                        class="item-summary mb-md-1 m-0"
                        :average="currentVersion.reviewsStats.average"
                        :text="'('+currentVersion.reviewsStats.count+' avis)'"
                        :rating-results-height="1.375"
                        link="#anchor-reviews"
                        @ob1-rating-link.prevent="scrollToPageMenuAnchor('anchor-reviews')"
                    />
                </div>
                <div class="d-flex flex-column flex-md-row bg-white">
                    <!--/* img */-->
                    <div class="d-flex flex-column info-device-width border-right-md border-gray2">
                        <div class="px-1 px-lg-2 py-1">
                            <div class="item-img">
                                <ob1-carrousel
                                    :id="'device-gallery'"
                                    :key="'device-gallery-' + currentVersion.seoId"
                                    :images="currentVersion.images"
                                    :is-product-sheet="true"
                                    :is-device-product-sheet="true"
                                    :with-zoom="true"
                                    :with-thumbnails="true"
                                />
                            </div>
                            <ob1-separateur v-if="currentDetailsForIntention.asilage" class="my-1" />
                            <asilage
                                v-if="currentDetailsForIntention.asilage"
                                :title="currentDetailsForIntention.asilage.title"
                                :condition="currentDetailsForIntention.asilage.condition"
                                :form-url="currentDetailsForIntention.asilage.formUrl"
                                :image="currentDetailsForIntention.asilage.image"
                                :condition-label="currentDetailsForIntention.asilage.conditionLabel"
                            />
                        </div>
                    </div>
                    <div class="w-100">
                        <div class="px-1 px-md-2 py-1">
                            <fieldset>
                                <legend class="visually-hidden">
                                    Couleur
                                </legend>
                                <ColorChips
                                    :id="'colors'"
                                    class="mb-2"
                                    :selected-color="currentVersion.color ? currentVersion.color.code : null"
                                    :colors="allColors"
                                    @change="changeVariant('color', $event.code)"
                                />
                            </fieldset>

                            <fieldset
                                v-for="(variant, index) in dto.allVariants"
                                :key="'variant-' + index"
                                :class="{ 'mt-1': !numberOfColors }"
                            >
                                <legend class="visually-hidden">
                                    {{ variant.name }}
                                </legend>
                                <ob1-chips
                                    v-for="(value, valueIndex) in variant.values"
                                    :id="'variant-' + index + '-' + valueIndex"
                                    :key="'variant-' + index + '-' + valueIndex"
                                    :is-choice-version="true"
                                    :label="value"
                                    :is-disabled="allGrades.length === 0 && index === dto.allVariants.length - 1 && !hasVariantEnabled(variant.name, value)"
                                    :is-checked="hasCurrentVersionVariantChecked(variant.name, value)"
                                    :disable-uncheck="true"
                                    additional-classes="mb-1"
                                    @ob1-chips-click="changeVariant(variant.name, value)"
                                />
                            </fieldset>

                            <fieldset v-if="allGrades.length > 0" class="mt-1">
                                <legend class="visually-hidden">
                                    Etat du mobile Reconditionné
                                </legend>
                                <ob1-chips
                                    v-for="(grade, index) in allGrades"
                                    :id="'grade-' + index"
                                    :key="'grade-' + index"
                                    :is-choice-version="true"
                                    :label="grade.label"
                                    :is-disabled="false"
                                    :is-checked="grade.isChecked"
                                    additional-classes="mb-1"
                                    :disable-uncheck="true"
                                    @ob1-chips-click="changeVariant('grade', grade.label)"
                                />
                            </fieldset>

                            <template v-if="allGrades.length > 0">
                                <ob1-liens
                                    text="Pour en savoir plus, c'est par ici"
                                    is-button
                                    data-bs-toggle="modal"
                                    modal-id="gradeModal"
                                    with-arrow
                                    data-oevent-category="comment_choisir_son_reconditionne"
                                    :data-oevent-action="getGradePopinAction()"
                                    data-oevent-label="vers_popin"
                                />
                                <grade-popin
                                    :modal-id="'gradeModal'"
                                    :default-grade-id="checkedGrade?.code"
                                    :labels="gradeLabels"
                                />
                            </template>

                            <fieldset v-if="allBatteries.length > 0" class="mt-1">
                                <legend class="visually-hidden">
                                    Etat de la batterie
                                </legend>
                                <ob1-chips
                                    v-for="(battery, index) in allBatteries"
                                    :id="'battery-' + index"
                                    :key="'battery-' + index"
                                    :is-choice-version="true"
                                    :label="battery.label"
                                    :is-disabled="!hasVariantEnabled('battery', battery.label)"
                                    :is-checked="battery.isChecked"
                                    additional-classes="mb-1"
                                    :disable-uncheck="true"
                                    @ob1-chips-click="changeVariant('battery', battery.label)"
                                />
                            </fieldset>

                            <ob1-separateur class="my-1" />

                            <template v-if="currentDetailsForIntention">
                                <div class="d-lg-flex justify-content-between">
                                    <device-detail-price
                                        :device-price="currentDetailsForIntention.price"
                                        :is-price-inversion-enabled="dto.priceInversionEnabled"
                                        :loan-rate-zero-label="dto.loanRateZeroLabel"
                                        :loan-rate-zero-offer-label="dto.loanRateZeroOfferLabel"
                                        :funding-details="fundingDetails"
                                        :show-guarantee-legal-mentions="currentVersion.showGuaranteeLegalMentions"
                                        :guarantee-legal-mentions-label="currentVersion.guaranteeLegalMentionsLabel"
                                        :commitment="currentDetailsForIntention.commitment"
                                        :complementary-price-label="currentDetailsForIntention.complementaryPriceLabel"
                                        :device-name="fundingDetailDeviceName"
                                        :advantage-messages="dto.advantageMessages"
                                    />
                                </div>

                                <refunds
                                    v-if="advantages"
                                    class="mb-1"
                                    :odrs="advantages.odrData"
                                    :device-seo-id="currentVersion.seoId"
                                    :refund-messages="dto.refundMessages"
                                    :recovery="advantages.recovery"
                                    :total-amount-in-cents="advantages.totalAmountInCents"
                                    :advantages-label="advantages.label"
                                    :simulator-modal-id="simulatorModalId"
                                    :simulator-result-modal-id="savedSimulationResultModalId"
                                    :recovery-modal-id="recoveryModalId"
                                />
                                <template v-if="hasRecovery">
                                    <mobile-recovery-simulator-modal
                                        :modal-id="simulatorModalId"
                                        :shop="shop"
                                        :messages="dto.refundMessages.recoverySimulator"
                                        @update-recovery-simulation="updateRecoveryPrice"
                                    />
                                    <mobile-recovery-simulation-result-modal
                                        v-if="advantages.recovery.manualEstimation"
                                        :modal-id="savedSimulationResultModalId"
                                        :shop="shop"
                                        :messages="dto.refundMessages.recoverySimulator.result"
                                        :estimated-device-price-in-cents="advantages.recovery.amountInCents"
                                        :estimated-device-model="advantages.recovery.deviceModel"
                                        :bonus="advantages.recovery.bonus"
                                        :online-recovery-available="onlineRecoveryAvailable"
                                        @new-simulation="newSimulation"
                                        @save-simulation="saveSimulation"
                                    />
                                    <mobile-recovery-simulation-result-modal
                                        :modal-id="currentSimulationResultModalId"
                                        :shop="shop"
                                        :messages="dto.refundMessages.recoverySimulator.result"
                                        :estimated-device-price-in-cents="estimatedDevice.recoveryPriceInCents"
                                        :estimated-device-model="estimatedDevice.model"
                                        :bonus="advantages.recovery.bonus"
                                        :online-recovery-available="onlineRecoveryAvailable"
                                        @new-simulation="newSimulation"
                                        @save-simulation="saveSimulation"
                                    />
                                    <mobile-recovery-details-modal
                                        :modal-id="recoveryModalId"
                                        :infos-recovery="dto.refundMessages.recoveryModal.infosRecovery"
                                        :online-recovery="onlineRecoveryAvailable ? dto.refundMessages.recoveryModal.onlineRecovery: null"
                                        :store-recovery="eligibleEresa ? dto.refundMessages.recoveryModal.storeRecovery : null"
                                        :bonus-recovery="dto.refundMessages.recoveryModal.bonusRecovery"
                                        :bonus="advantages.recovery.bonus"
                                        :recovery-simulator="dto.refundMessages.recoverySimulator"
                                        :simulator-button-label="dto.refundMessages.simulatorButtonLabel"
                                        :bonus-amount="bonusAmountFormat"
                                        :simulator-modal-id="simulatorModalId"
                                    />
                                </template>
                                <template v-if="currentDetailsForIntention.preorder">
                                    <p class="col-12 mb-sm-0">
                                        <strong>En précommande en ligne</strong>
                                    </p>
                                </template>
                                <div v-if="isLoading" class="item-spinner d-flex justify-content-center">
                                    <ob1-progress-indicators :size="50" type="undetermined" />
                                </div>
                                <template v-if="currentDetailsForIntention.chooseOfferLinks != null">
                                    <!--/* CTA vers sélecteur d'offre qui peut afficher la popin d'authent */-->
                                    <Bouton
                                        ref="add-device-cta"
                                        class="mt-1"
                                        :label="ctaLabel"
                                        :is-primary="true"
                                        :tms-category="needAuthentication ? 'popin_poursuivre_votre_commande' : 'fiche_produit'"
                                        :tms-action="ctaLabel"
                                        :tms-label="needAuthentication ? null : currentDetailsForIntention.chooseOfferLinks.link"
                                        :href="needAuthentication? null : currentDetailsForIntention.chooseOfferLinks.link"
                                        :is-button="needAuthentication"
                                        :data-bs-toggle="needAuthentication ? 'modal' : null"
                                        :data-bs-target="needAuthentication? '#authenticationPopIn' : null"
                                        @click="handleCtaClick"
                                    />
                                    <!--/* Popin d'authent  */-->
                                    <authentication-popin
                                        v-if="dto.authenticationPopinLabelsDto"
                                        :id="'authenticationPopIn'"
                                        :title="dto.authenticationPopinLabelsDto.title"
                                        :subtitle="dto.authenticationPopinLabelsDto.subtitle"
                                        :new-client-title="dto.authenticationPopinLabelsDto.newClientTitle"
                                        :new-client-description="dto.authenticationPopinLabelsDto.newClientDescription"
                                        :new-client-href="currentDetailsForIntention.chooseOfferLinks.link"
                                        :client-title="dto.authenticationPopinLabelsDto.alreadyClientTitle"
                                        :client-description="dto.authenticationPopinLabelsDto.alreadyClientDescription"
                                        :client-href="currentDetailsForIntention.chooseOfferLinks.authenticatedLink"
                                    />
                                </template>
                                <template v-else-if="currentDetailsForIntention.orderLinks != null">
                                    <!--/* Popin d'authent  */-->
                                    <authentication-popin
                                        v-if="dto.authenticationPopinLabelsDto && currentDetailsForIntention.orderLinks.onlineAuthenticatedOrderHref"
                                        :id="'authenticationPopIn'"
                                        :title="dto.authenticationPopinLabelsDto.title"
                                        :subtitle="dto.authenticationPopinLabelsDto.subtitle"
                                        :new-client-title="dto.authenticationPopinLabelsDto.newClientTitle"
                                        :new-client-description="dto.authenticationPopinLabelsDto.newClientDescription"
                                        :new-client-href="currentDetailsForIntention.orderLinks.onlineOrderHref"
                                        :client-title="dto.authenticationPopinLabelsDto.alreadyClientTitle"
                                        :client-description="dto.authenticationPopinLabelsDto.alreadyClientDescription"
                                        :client-href="currentDetailsForIntention.orderLinks.onlineAuthenticatedOrderHref"
                                    />
                                    <template v-if="currentDetailsForIntention.orderLinks.eresaOrderHref && currentDetailsForIntention.orderLinks.onlineOrderHref">
                                        <!--/* CTA vers la popin de choix de parcours. */-->
                                        <Bouton
                                            ref="add-device-cta"
                                            class="mt-1"
                                            :label="ctaLabel"
                                            :is-primary="true"
                                            tms-category="popin_poursuivre_votre_commande"
                                            :tms-action="ctaLabel"
                                            tms-label=""
                                            is-button
                                            data-bs-toggle="modal"
                                            data-bs-target="#allDisponibilitiesPopIn"
                                        />

                                        <!--/* Popin choix en ligne/Click & Collect */-->
                                        <!--/* Peut débrayer sur la popin d'authent */-->
                                        <all-disponibilities-popin
                                            id="allDisponibilitiesPopIn"
                                            :title="dto.allDisponibilitiesPopinLabelsDto.title"
                                            :tdc-section="dto.allDisponibilitiesPopinLabelsDto.tdcSection"
                                            :eresa-section="dto.allDisponibilitiesPopinLabelsDto.eresaSection"
                                            :eco-foot-note="dto.allDisponibilitiesPopinLabelsDto.ecoFootNote"
                                            :tdc-href="needAuthentication ? null : currentDetailsForIntention.orderLinks.onlineOrderHref"
                                            :eresa-href="currentDetailsForIntention.orderLinks.eresaOrderHref"
                                            :chain-to-auth-on-online="needAuthentication"
                                            @online-click-requires-auth="openAuthenticationAfterDisponibilities"
                                        />
                                    </template>
                                    <template v-else-if="currentDetailsForIntention.orderLinks.eresaOrderHref">
                                        <!--/* CTA vers commande eresa */-->
                                        <Bouton
                                            ref="add-device-cta"
                                            class="mt-1"
                                            :label="ctaLabel"
                                            :is-primary="true"
                                            :tms-category="'fiche_produit'"
                                            :tms-action="ctaLabel"
                                            :tms-label="currentDetailsForIntention.orderLinks.eresaOrderHref"
                                            :href="currentDetailsForIntention.orderLinks.eresaOrderHref"
                                            :is-button="false"
                                            @click="handleCtaClick"
                                        />
                                    </template>
                                    <template v-else-if="currentDetailsForIntention.orderLinks.onlineOrderHref">
                                        <!--/* CTA vers commande en ligne */-->
                                        <Bouton
                                            ref="add-device-cta"
                                            class="mt-1"
                                            :label="ctaLabel"
                                            :is-primary="true"
                                            :tms-category="needAuthentication? 'popin_poursuivre_votre_commande' : 'fiche_produit'"
                                            :tms-action="ctaLabel"
                                            :tms-label="currentDetailsForIntention.orderLinks.onlineOrderHref"
                                            :href="needAuthentication ? null : currentDetailsForIntention.orderLinks.onlineOrderHref"
                                            :is-button="needAuthentication"
                                            :data-bs-toggle="needAuthentication? 'modal' : null"
                                            :data-bs-target="needAuthentication ? '#authenticationPopIn' : null"
                                            @click="handleCtaClick"
                                        />
                                    </template>
                                </template>
                                <div
                                    v-else
                                    class="row"
                                >
                                    <ob1-message-alerte
                                        class="col-12 pt-2"
                                        :is-simple-alerte="false"
                                        :title="dto.alertStatusMessages.title"
                                        :text="dto.alertStatusMessages.message"
                                        type="warning"
                                    />
                                </div>

                                <div class="mt-2">
                                    <sold-by-card
                                        v-if="dto.showSoldByInfos"
                                        :title="sellerLabel"
                                        :sold-by-orange="currentVersion.firstParty"
                                        :info-lines="deliveryLabel"
                                    />
                                    <template v-else>
                                        <availability-mention
                                            v-for="info in deliveryLabel"
                                            :key="info.label"
                                            :available="info.status === 'SUCCESS'"
                                            :label="info.label"
                                        />
                                    </template>
                                </div>
                            </template>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!--/* DAS / Batterie / Energy-Label */-->
        <div v-if="shouldShowDasBatteryOrEnergyLabelBlock" class="bg-gray2">
            <div class="container-fluid">
                <div class="row align-items-center pb-1">
                    <!--/* DAS */-->
                    <div v-if="currentVersion.das" class="col-12 col-md-auto">
                        <das
                            class="my-1"
                            :head="currentVersion.das.head"
                            :trunk="currentVersion.das.trunk"
                            :member="currentVersion.das.member"
                            :prefix="currentVersion.das.prefix"
                            :popin-title="currentVersion.das.popinTitle"
                            :popin-text="currentVersion.das.popinDescription"
                        />
                    </div>
                    <!--/* Batterie(Charger) */-->
                    <template v-if="currentVersion.chargerInfos">
                        <div v-if="currentVersion.chargerInfos.chargerIncluded" class="col-12 col-md-auto my-1 my-md-0 mx-md-2 px-md-0">
                            <div class="d-flex align-items-center">
                                <div class="icon icon-charger-availability charger-icon-size ps-1 ps-md-0 text-gray4" aria-hidden="true" />
                                <strong class="ps-3 ps-md-0" v-html="currentVersion.chargerInfos.chargerIncludedMessage" />
                            </div>
                        </div>
                        <div v-else-if="currentVersion.chargerInfos.chargerCompatibleInfos" class="col-12 col-md-auto my-1 my-md-0 mx-md-2 px-md-0">
                            <ChargerInfos :charger-infos="currentVersion.chargerInfos.chargerCompatibleInfos" />
                        </div>
                    </template>
                    <!--/* Energy-Label */-->
                    <div v-if="currentVersion.energyLabel" class="col-12 col-md-auto">
                        <energy-label :id="currentVersion.seoId" :energy-label="currentVersion.energyLabel" />
                    </div>
                </div>
            </div>
        </div>

        <!--/* Financing */-->
        <section v-if="shouldDisplayPXFMention" class="container-fluid">
            <pxf-description
                :pxf-legal-mentions-text="dto.pxfMention"
            />
            <ob1-separateur
                class="my-2 mb-xl-3"
            />
            <div class="row mt-2 mb-1">
                <div class="col-12">
                    <financing-mentions :financing-mentions="dto.financingMentions" />
                </div>
            </div>
            <ob1-separateur
                class="my-2 mb-1"
            />
        </section>

        <div class="container-fluid mt-2">
            <p v-if="currentVersion.descriptionTitle" class="lead text-mw mb-0">
                <strong>{{ currentVersion.descriptionTitle }}</strong>
            </p>
            <p v-if="currentVersion.description" class="text-mw" v-html="currentVersion.description" />

            <!--/* Page Menu */-->
            <ob1-page-menu
                v-if="pageMenu.tabs.length > 0"
                id="device-details-page-menu"
                :tabs="tabs"
                class="sticky bg-white mb-2"
                @ob1-page-menu-click="scrollToPageMenuAnchor"
            />

            <!--/* Zigzag */-->
            <section v-if="currentVersion.zigZag || currentVersion.longDescription">
                <div class="mb-xl-3" />
                <header-signature :text="essentialTitle" />
                <EditorialPush v-if="currentVersion.zigZag" :zigzag="currentVersion.zigZag" :with-image-resize="!currentVersion.firstParty" />
                <div v-else-if="currentVersion.longDescription">
                    <p id="anchor-zig-zag" v-html="currentVersion.longDescription" />
                </div>
            </section>

            <!--/* Fiche Technique */-->
            <section v-if="currentVersion.technicalFeatures">
                <ob1-separateur v-if="currentVersion.zigZag || currentVersion.longDescription" class="my-2 mb-xl-3" />
                <device-technical-features
                    :device-features="currentVersion.technicalFeatures"
                />
            </section>

            <!--/* La Reprise */-->
            <template v-if="hasRecovery">
                <section class="mb-2 mb-md-3">
                    <ob1-separateur class="my-2" />
                    <header-signature :text="recoveryMessages.messages.title" />
                    <mobile-recovery
                        id="anchor-mobile-recovery"
                        :recovery-amount-in-cents="advantages.recovery.amountInCents"
                        :recovery-device-model="advantages.recovery.deviceModel"
                        :manual-estimation="advantages.recovery.manualEstimation"
                        :bonus-amount-in-cents="bonusAmountInCents"
                        :simulator-modal-id="simulatorModalId"
                        :recovery-modal-id="recoveryModalId"
                        :messages="recoveryMessages.messages"
                        :visual="recoveryMessages.visual"
                        :shop="shop"
                    />
                </section>
            </template>

            <!--/* Les garanties légales */-->
            <section v-if="currentVersion.showGuaranteeLegalMentions">
                <ob1-separateur class="my-2 mb-xl-3" />
                <guarantee-legal-mentions :legal-mentions="currentVersion.deviceLegalMentions" />
            </section>

            <!--/* Eco Rating */-->
            <section v-if="currentVersion.ecoRating">
                <eco-rating :eco-rating="currentVersion.ecoRating" />
            </section>

            <!--/* Les états esthétiques */-->
            <section v-if="retailMention">
                <ob1-separateur class="my-2 mb-xl-3" />
                <header-signature :text="dto.retailMentionTitle" />
                <div id="anchor-reconditioned" class="row">
                    <div class="col-12 mb-1">
                        <div v-html="retailMention" />
                    </div>
                </div>
            </section>

            <!--/* Avis clients */-->
            <template v-if="displayRatings">
                <section class="mb-md-3">
                    <ob1-separateur v-if="currentVersion.showGuaranteeLegalMentions" class="my-2" />
                    <header-signature :text="reviewsMessages.title" />
                    <device-average-rating-reviews
                        :reviews-stats-average="currentVersion.reviewsStats.average"
                        :review-count="currentVersion.reviewsStats.count"
                        :reviews-messages="reviewsMessages"
                        :summary="currentVersion.summary"
                    />
                    <device-filter-and-details-reviews
                        :device-details-reviews="deviceDetailsReviews"
                        :total-review-count="currentVersion.reviewsStats.count"
                        :device-filter-reviews="deviceFilterReviews"
                        :reviews-messages="reviewsMessages"
                        :ratings-filter-checkboxes="ratingsFilterCheckboxes"
                        :date-sort-radio-buttons="dateSortRadioButtons"
                        @update-ratings-filter-handler="updateRatingsFilterHandler"
                        @review-date-sort-item-click="reviewDateSortHandler"
                        @more-device-reviews="getMoreDeviceReviews"
                    />
                </section>
            </template>

            <!--/* Zone environmental */-->
            <ob1-liens
                v-if="currentVersion.environmentalQuality"
                class="mw-100 py-0"
                :href="currentVersion.environmentalQuality.url"
                :text="currentVersion.environmentalQuality.linkLabel"
                :with-arrow="true"
                :is-mini="true"
                target="_blank"
            />
        </div>
        <div
            v-if="dto.freeHandsText || shouldDisplay4xCBMention"
            class="bg-gray2 mt-1 pt-1 pb-1"
        >
            <div class="container-fluid">
                <!--/* Zone Kit mains libres */-->
                <free-hands
                    :text="dto.freeHandsText"
                />
                <legal-mentions4x-cb
                    v-if="shouldDisplay4xCBMention"
                    :label="dto.cb4xMention.label"
                    :popin4x-cb="dto.cb4xMention.cb4xPopin"
                />
            </div>
        </div>

        <!--/* Zone Engagements */-->
        <section v-if="currentVersion.firstParty">
            <div v-if="isBrandSosh" class="container-fluid">
                <sosh-commitments :commitments="dto.soshCommitments" />
            </div>
            <div v-else class="bg-dark d-webview-none">
                <div class="container-fluid">
                    <orange-commitments :commitments="dto.orangeCommitments" />
                </div>
            </div>
        </section>

        <!-- snack bar pour afficher le message de confirmation d'enregistrement de la simulation de reprise -->
        <ob1-snack-bar
            id="simulation-snackbar"
            ref="savedSimulationSnackbar"
            icon="icon-tick-circle"
            :label="dto.refundMessages.recoverySimulator.result.snackbarLabel"
        />
    </div>
</template>

<script>

import {VersionSorter} from "../common/mixins/VersionSorter";
import {ColorSorter} from "../common/mixins/ColorSorter";
import DeviceDetailPrice from "./components/DeviceDetailPrice.vue";
import Asilage from "../common/components/Asilage.vue";
import AvailabilityMention from "../common/mobiledetails/AvailabilityMention.vue";
import Refunds from "../common/components/Refunds.vue";
import DeviceFilterAndDetailsReviews from "../common/devicereviews/DeviceFilterAndDetailsReviews.vue";
import HeaderSignature from "../../common/components/vue/HeaderSignature.vue";
import DeviceAverageRatingReviews from "../common/devicereviews/DeviceAverageRatingReviews.vue";
import {scrollToElement} from "../common/Util";
import LegalMentions4xCb from "../common/components/LegalMentions4xCb.vue";
import FinancingMentions from "../common/components/FinancingMentions.vue";
import PxfDescription from "../../common/fragments/mobile/PxfDescription.vue";
import GuaranteeLegalMentions from "../common/components/GuaranteeLegalMentions.vue";
import ChargerInfos from "../common/ChargerInfos.vue";
import EnergyLabel from "../common/EnergyLabel.vue";
import Das from "../common/components/Das.vue";
import SoldByCard from "../../common/components/custom/SoldByCard.vue";
import {InfoLineStatus} from "../../common/components/custom/models/InfoLine";
import EditorialPush from "../../common/components/ob1/EditorialPush.vue";
import Rating from "../../common/components/ob1/Rating.vue";
import DeviceTechnicalFeatures from "./components/DeviceTechnicalFeatures.vue";
import MobileRecoverySimulatorModal from "../common/MobileRecoverySimulatorModal.vue";
import MobileRecoveryDetailsModal from "../common/MobileRecoveryDetailsModal.vue";
import MobileRecoverySimulationResultModal from "../common/MobileRecoverySimulationResultModal.vue";
import {toTmsFormat} from "../../common/utils/TmsUtils";
import ColorChips from "../../common/components/ob1/ColorChips.vue";
import GradePopin from "../common/components/GradePopin.vue";
import ContractPhoneLines from "../common/ContractPhoneLines.vue";
import Incentive from "../common/components/Incentive.vue";
import IntentionSwitch from "../common/components/IntentionSwitch.vue";
import CustomerPopin from "../common/components/CustomerPopin.vue";
import DigitalCartTooltip from "../../common/components/vue/tooltip/DigitalCartTooltip.vue";
import SelectedOffer from "../common/SelectedOffer.vue";
import Bouton from "../../common/components/ob1/Bouton.vue";
import AuthenticationPopin from "../common/mobiledetails/AuthenticationPopin.vue";
import AllDisponibilitiesPopin from "../common/components/AllDisponibilitiesPopin.vue";
import FreeHands from "../../common/components/FreeHands.vue";
import OrangeCommitments from "../common/components/OrangeCommitments.vue";
import SoshCommitments from "../common/components/SoshCommitments.vue";
import EcoRating from "../common/components/EcoRating.vue";
import MobileRecovery from "../common/mobilerecovery/MobileRecovery.vue";

const dto = JSON.parse(document.getElementById("device-phone-details").dataset.devicePhoneDetails);

const DeviceAddedState = Object.freeze({
    LOADED: 1,
    LOADING: 2,
})

const intentionParam = 'intention';
const ACQUISITION_PARAM = "acquisition";
const RENOUVELLEMENT_PARAM = "renouvellement";
const TSEUL_PARAM = "terminal-seul";
const PCM_PARAM = "pcm";
const PCMOA_PARAM = "pcmoa";
const CHANGER_DE_MOBILE_PARAM = "changer-de-mobile";


export default {
    name: "DevicePhoneDetails",
    components: {
        MobileRecovery,
        EcoRating,
        SoshCommitments, OrangeCommitments,
        FreeHands,
        Bouton,
        AllDisponibilitiesPopin,
        AuthenticationPopin,
        SelectedOffer,
        DigitalCartTooltip,
        CustomerPopin, IntentionSwitch, Incentive, ContractPhoneLines,
        GradePopin,
        ColorChips,
        Rating,
        EditorialPush,
        SoldByCard,
        Das, EnergyLabel, ChargerInfos,
        DeviceTechnicalFeatures,
        PxfDescription, FinancingMentions,
        LegalMentions4xCb,
        Refunds,
        MobileRecoverySimulationResultModal,
        MobileRecoveryDetailsModal,
        MobileRecoverySimulatorModal,
        DeviceAverageRatingReviews,
        HeaderSignature,
        DeviceFilterAndDetailsReviews,
        Asilage,
        DeviceDetailPrice,
        AvailabilityMention,
        GuaranteeLegalMentions
    },
    mixins: [ColorSorter, VersionSorter],
    data() {
        const urlParams = new URLSearchParams(window.location.search);
        const intentionFromUrl = urlParams.get(intentionParam);

        return {
            dto: dto,
            brand: dto.brand,
            deviceBrand: dto.deviceBrand,
            intentionParam: dto.defaultIntention,
            loading: true,
            currentVersion: dto.currentVersion,
            detailsForIntention: Object.fromEntries(
                dto.allVersions.map(version => [version.seoId, version.versionIntentionDetails])
            ),
            allVersions: dto.allVersions,
            versionsCapacities: dto.allCapacities,
            fundingDetails: dto.fundingDetails,
            breadcrumbItems: dto.filAriane.items,
            essentialTitle: dto.essentialTitle,
            pageMenu: dto.pageMenu,
            reviewsMessages: dto.reviewsMessages,
            reviewDateSortParam: dto.reviewDateSort ?? "date-desc",
            deviceDetailsReviews: {
                cursor: undefined,
                reviews: [],
                loading: true
            },
            deviceFilterReviews: {
                ratingsFilter: [],
                loading: true
            },
            selectedRatingsFilter: [],
            state: DeviceAddedState.LOADED,
            shop: dto.shop,
            intention: dto.intention,
            estimatedDevice: {
                recoveryPriceInCents: undefined,
                model: undefined
            },
            simulatorModalId: "mobile-recovery-simulator",
            currentSimulationResultModalId: "current-mobile-recovery-simulation-result",
            savedSimulationResultModalId: "saved-mobile-recovery-simulation-result",
            recoveryModalId: "mobile-recovery-popin",
            gradeLabels: dto.gradeLabels,
            displayCustomerPopin: intentionFromUrl === CHANGER_DE_MOBILE_PARAM && dto.customerPopin,
            selectedOffer: undefined,
            changerMobileLink: dto.changerMobileLink,
            recoveryMessages: dto.recoveryMessages
        };
    },
    computed: {
        isBrandSosh() {
            return this.brand === 'SOSH';
        },
        isBrandOrange() {
            return this.brand === 'ORANGE';
        },
        currentDetailsForIntention() {
            return this.detailsForIntention?.[this.currentVersion.seoId] ?? null;
        },
        advantages() {
            return this.currentDetailsForIntention?.advantages;
        },
        eligibleEresa() {
            return this.currentDetailsForIntention?.eligibleEresa ?? false;
        },
        onlineRecoveryAvailable() {
            return this.currentDetailsForIntention?.onlineRecoveryAvailable ?? false;
        },
        allColors: function () {
            const colors = [];
            for (const version of this.allVersions) {
                const versionColor = version.color;
                if (versionColor && !colors.find(color => color.code === versionColor.code)) {
                    colors.push(
                        {
                            colorName: versionColor.colorName,
                            code: versionColor.code,
                            isChecked: this.checkedColor && this.checkedColor.code === versionColor.code
                        }
                    );
                }
            }
            return this.sortColors(colors);
        },
        allGrades: function () {
            const grades = [];
            for (const version of this.allVersions) {
                const versionGrade = version.grade;
                if (versionGrade && !grades.find(grade => grade.label === versionGrade.label)) {
                    grades.push(
                        {
                            label: versionGrade.label,
                            isChecked: this.checkedGrade && this.checkedGrade.label === versionGrade.label,
                            sortOrder: versionGrade.sortOrder
                        }
                    );
                }
            }
            return grades.sort((a, b) => a.sortOrder - b.sortOrder);
        },
        allBatteries: function () {
            const batteries = [];
            for (const version of this.allVersions) {
                const versionBattery = version.battery;
                if (versionBattery && !batteries.find(battery => battery.label === versionBattery.label)) {
                    batteries.push(
                        {
                            label: versionBattery.label,
                            isChecked: this.checkedBattery && this.checkedBattery.label === versionBattery.label,
                            sortOrder: versionBattery.sortOrder
                        }
                    );
                }
            }
            return batteries.sort((a, b) => a.sortOrder - b.sortOrder);
        },
        bonusAmountFormat() {
            let amountInCents = this.bonusAmountInCents;
            if (amountInCents) {
                return PriceFormatter.formatEurosWithSpace(amountInCents / 100, false).toString();
            } else {
                return null;
            }
        },
        bonusAmountInCents() {
            return this.advantages?.recovery?.bonus?.amountInCents;
        },
        checkedColor: function () {
            return this.currentVersion.color;
        },
        checkedGrade: function () {
            return this.currentVersion.grade;
        },
        checkedBattery: function () {
            return this.currentVersion.battery;
        },
        displayRatings() {
            return !!this.currentVersion.reviewsStats;
        },
        numberOfColors: function () {
            return this.allVersions.filter(version => version.color !== undefined).length;
        },
        pageMenuOffsetHeight: function () {
            if (document.querySelector('#device-details-page-menu')) {
                return document.querySelector('#device-details-page-menu').offsetHeight;
            }
            return 0;
        },
        ratingsFilterCheckboxes() {
            return this.deviceFilterReviews.ratingsFilter.map(rating => {
                return {
                    label: rating.label,
                    isChecked: this.selectedRatingsFilter.includes(rating.id),
                    count: rating.count,
                    id: rating.id,
                }
            })
        },
        sellerLabel() {
            return this.currentVersion.seller.url
                ? `Vendu et expédié par <b><a href="${this.currentVersion.seller.url}" target="_blank">${this.currentVersion.seller.name}</a></b>`
                : `Vendu et expédié par <b>${this.currentVersion.seller.name}</b>`
        },
        deliveryLabel() {
            const labels = this.currentDetailsForIntention?.earliestDeliveryDate
                ? (this.currentDetailsForIntention.inStock
                    ? [{ status: InfoLineStatus.SUCCESS, label: `Livré à partir du ${this.currentDetailsForIntention.earliestDeliveryDate}` }]
                    : [{ status: InfoLineStatus.WARNING, label: 'Indisponible en ligne' }])
                : [];

            if (this.currentVersion.firstParty) {
                labels.push(this.eligibleEresa
                    ? { status: InfoLineStatus.SUCCESS, label: 'Disponible en Click & Collect' }
                    : { status: InfoLineStatus.WARNING, label: 'Indisponible en Click & Collect' }
                )
            }

            return labels
        },
        shouldDisplayPXFMention() {
            return this.currentDetailsForIntention?.price?.price?.paymentInfoLabel && this.currentDetailsForIntention.price.price.paymentInfoLabel.type === 'PXF';
        },
        shouldDisplay4xCBMention() {
            return this.currentDetailsForIntention?.price?.price?.paymentInfoLabel && this.currentDetailsForIntention.price.price.paymentInfoLabel.type === 'CB4X';
        },
        tabs() {
            const hiddenTabs = [];
            if (!this.currentVersion.zigZag && !this.currentVersion.longDescription) {
                hiddenTabs.push('anchor-zig-zag');
            }
            if (!this.currentVersion.technicalFeatures) {
                hiddenTabs.push('anchor-technical-sheet');
            }
            if (!this.currentVersion.showGuaranteeLegalMentions) {
                hiddenTabs.push('anchor-guarantee-legal-mentions');
            }
            if (!this.currentVersion.reviewsStats) {
                hiddenTabs.push('anchor-reviews');
            }
            if (!this.hasRecovery) {
                hiddenTabs.push('anchor-mobile-recovery');
            }
            if (!this.retailMention) {
                hiddenTabs.push('anchor-reconditioned');
            }
            if (!this.currentVersion.ecoRating) {
                hiddenTabs.push('anchor-eco-rating');
            }
            return this.pageMenu.tabs.filter(tab => !hiddenTabs.includes(tab.id));
        },
        retailMention() {
            return this.currentVersion.deviceLegalMentions?.retailMentiontext
        },
        isLoading() {
            return this.state === DeviceAddedState.LOADING;
        },
        shouldShowDasBatteryOrEnergyLabelBlock() {
            return this.currentVersion?.chargerInfos || this.currentVersion?.energyLabel || this.currentVersion?.das;
        },
        dateSortRadioButtons() {
            const sortOptions = [
                { id: "date-asc", value: "date-asc", ariaLabelledBy: "date-croissante", primaryText: this.reviewsMessages.sortAscending },
                { id: "date-desc", value: "date-desc", ariaLabelledBy: "date-decroissante", primaryText: this.reviewsMessages.sortDescending }
            ];

            return sortOptions.map(option => ({
                radioButtonDTO: {
                    id: option.id,
                    name: "tri",
                    label: "",
                    checked: this.reviewDateSortParam === option.id,
                    ariaLabelledBy: option.ariaLabelledBy,
                    value: option.value
                },
                primaryText: option.primaryText
            }));
        },
        bannerAndBrand() {
            if (this.currentDetailsForIntention?.price?.banner && this.dto.deviceBrand) {
                return `${this.currentDetailsForIntention.price.banner} - ${this.dto.deviceBrand.toUpperCase()}`;
            } else if (this.currentDetailsForIntention?.price?.banner) {
                return `${this.currentDetailsForIntention.price.banner}`;
            } else if (this.dto.deviceBrand) {
                return `${this.dto.deviceBrand.toUpperCase()}`;
            }
            return '';
        },
        hasRecovery() {
            return this.currentDetailsForIntention?.advantages?.recovery != null;
        },
        ctaLabel() {
            return (this.intentionParam === ACQUISITION_PARAM && this.dto.deviceSeoIdFromCart != null && this.dto.deviceSeoIdFromCart !== this.currentVersion.seoId)
                ? 'Remplacer par ce mobile'
                : 'Continuer';
        },
        needAuthentication(){
            return !!this.dto.authenticationPopinLabelsDto && this.intentionParam === ACQUISITION_PARAM
        },
        fundingDetailDeviceName() {
            return this.dto.deviceBrand + ' ' + this.currentVersion.name;
        }
    },
    created: function () {
        document.addEventListener('scroll', () => {
            if (this.tabs && this.tabs.length > 0) {
                updatePageMenu('device-details-page-menu', this.tabs.map(tab => "#" + tab.id));
            }
        });
    },
    mounted() {
        jQuery(document).ready(()  => {
            // Gestion de la popin utilisateur
            const customerPopin = $('#customer-popin-id');
            if (this.displayCustomerPopin) {
                customerPopin.modal('show');
            }
            this.loadDeviceReviews();
        });
    },
    methods: {
        hasCurrentVersionVariantChecked(name, value) {
            return this.currentVersion.variants.some(variant => variant.name === name && variant.value === value);
        },
        findVersionByVariants(variants) {
            return this.allVersions.find(version =>
                variants.every(variant =>
                    version.variants.some(v => v.name === variant.name && v.value === variant.value)
                )
            );
        },
        findVersionByVariant(name, value) {
            return this.allVersions.find(version =>
                version.variants.some(v => v.name === name && v.value === value)
            );
        },
        hasVariantEnabled(name, value) {
            let currentVariants = this.currentVersion.variants;
            let newVariants = currentVariants.map(variant =>
                variant.name === name ? { ...variant, value: value }
                    : variant
            );
            if (!newVariants.some(variant => variant.name === name)) {
                newVariants.push({name: name, value: value});
            }
            return this.findVersionByVariants(newVariants);
        },
        changeVariant(variantName, variantValue) {
            let currentVariants = this.currentVersion.variants;
            let newVariants = currentVariants.map(variant =>
                variant.name === variantName ? { ...variant, value: variantValue }
                    : variant
            );
            let newVersion = this.findVersionByVariants(newVariants);
            if (newVersion) this.currentVersion = newVersion;
            else this.currentVersion = this.findVersionByVariant(variantName, variantValue);
            this.updateInfos();
        },
        loadDeviceDetails: function () {
            return new Promise((resolve, reject) => {
                this.loading = true;
                this.fetchDeviceDetails().then(result => {
                    this.loading = false;
                    this.detailsForIntention = result.detailsBySeoId;
                    resolve();
                }).catch((error) => {
                    if (window.MyShop?.crashLogReporter) {
                        let reporter = window.MyShop.crashLogReporter;
                        reporter.logError(error).then(() => {
                        });
                    }
                    this.loading = false;
                    window.location.href = this.dto.infoViewUrl
                    reject(error);
                });
            });
        },
        fetchDeviceDetails: function () {
            let url = this.dto.apiRootUrl;
            url = addParam(url, 'shop=' + this.shop);
            url = addParam(url, 'device=' + this.currentVersion.seoId);
            url = addParam(url, 'intention=' + this.intentionParam);
            if (this.intentionParam === this.dto.selectedOffer?.intention){
                url = addParam(url, 'offerSeoId=' + this.dto.selectedOffer.offerSeoId);
                url = addParam(url, 'offerFromCart=' + this.dto.selectedOffer.fromCart);
            }

            url = addParam(url, 'internetPartInCart=' + this.dto.internetPartInCart);

            if (this.intentionParam === RENOUVELLEMENT_PARAM || this.intentionParam === PCM_PARAM || this.intentionParam === PCMOA_PARAM) {
                url = addParam(url, 'clientContext=' + this.dto.clientContext);
                url = addParam(url, 'withPcmoa=' + this.dto.withPcmoa);
            }

            return new Promise((resolve, reject) => {
                $.ajax({
                    method: "GET",
                    xhrFields: {
                        withCredentials: true
                    },
                    crossDomain: true,
                    url: url,
                    contentType: "application/json",
                    dataType: "json"
                }).done(result => {
                    resolve(result)
                }).fail(error => {
                    reject(error)
                })
            });
        },
        updateRecoveryPrice(priceInCents, model) {
            this.estimatedDevice.recoveryPriceInCents = priceInCents;
            this.estimatedDevice.model = model;
            $("#" + this.simulatorModalId).modal('hide');
            $("#" + this.currentSimulationResultModalId).modal('show');
        },
        newSimulation() {
            $("#" + this.savedSimulationResultModalId).modal('hide');
            $("#" + this.currentSimulationResultModalId).modal('hide');
            $("#" + this.simulatorModalId).modal('show');
        },
        saveSimulation() {
            this.showSavedSimulationSnackbar();
            this.loadDeviceDetails();
        },
        showSavedSimulationSnackbar() {
            const snackbar = this.$refs.savedSimulationSnackbar;
            if (snackbar) {
                snackbar.show();
            }
        },
        updateInfos() {
            // On met à jour l'url
            this.updateUrl();
            // On met à jour le fil d'ariane
            this.updateBreadcrumb();
        },
        updateBreadcrumb() {
            const breadcrumbLength = this.breadcrumbItems.length
            this.breadcrumbItems.splice(breadcrumbLength - 1, 1, {name: this.currentVersion.name})
        },
        updateUrl() {
            // On met à jour l'url avec le seoId de la déclinaison sélectionnée
            const slug = window.location.href.substring(
                window.location.href.lastIndexOf("/") + 1
            );
            const parameterIndex = window.location.href.lastIndexOf("?");
            const parameters = parameterIndex === -1 ? "" : window.location.href.substring(parameterIndex);
            const fullUrlWithoutSlug = window.location.href.substring(
                0, window.location.href.lastIndexOf(slug)
            );
            const updatedUrl = fullUrlWithoutSlug + this.currentVersion.seoId + parameters;
            window.history.replaceState({}, null, updatedUrl);
        },
        switchIntention(selectedId) {

            if (this.dto.customerPopin && selectedId === "intention-changer-de-mobile") {
                $('#customer-popin-id').modal('show')
            } else if (this.isBrandOrange && selectedId === "intention-changer-de-mobile") {
                // On fait un reload de la page pour gérer les page d'erreur sur l'eligibilité au changement de mobile
                window.location.href = updateUrlWithParam(window.location.href, "intention", CHANGER_DE_MOBILE_PARAM);
                return;
            } else {
                if (selectedId === "intention-tseul") {
                    this.intentionParam = TSEUL_PARAM;
                } else if (selectedId === "intention-acquisition") {
                    this.intentionParam = ACQUISITION_PARAM;
                } else if (selectedId === "intention-changer-de-mobile") {
                    // On est sur sosh : on force l'intention renouvellement
                    this.intentionParam = RENOUVELLEMENT_PARAM;
                }
                this.updateUrlWithIntention();
                this.loadDeviceDetails().then(() => {
                    //this.updateBreadCrumbWithIntention();
                    this.updateDatalayerForIntention();
                });
            }
        },
        updateDatalayerForIntention() {
            if (tmsData) {
                let price = this.currentDetailsForIntention?.price?.price?.totalPriceWithPromo;

                if (price) {
                    tmsData.offre_prix_ttc = (price / 100).toString().replace(".", ",");
                } else tmsData.offre_prix_ttc = "";

                if (this.intentionParam === TSEUL_PARAM) {
                    tmsData.contexte_produit = "acquisition_terminal_seul"
                } else if (this.intentionParam === ACQUISITION_PARAM) {
                    if (this.selectedOffer?.offerSeoId) {
                        tmsData.contexte_produit = "acquisition_forfait_vers_terminal"
                    } else {
                        tmsData.contexte_produit = "acquisition_terminal_vers_forfait"
                    }
                } else if (this.intentionParam === PCM_PARAM) {
                    tmsData.contexte_produit = "changement_pcm"
                } else if (this.intentionParam === RENOUVELLEMENT_PARAM) {
                    tmsData.contexte_produit = "changement_renouvellement"
                }

                if (this.hasMobileBonusRecovery) {
                    tmsData.bonus_reprise = "true"
                } else {
                    tmsData.bonus_reprise = "false"
                }

                Tms.view(tmsData)
            }
        },
        updateUrlWithIntention() {
            if (this.intentionParam === RENOUVELLEMENT_PARAM || this.intentionParam === CHANGER_DE_MOBILE_PARAM) {
                return window.history.replaceState({}, null, updateUrlWithParam(window.location.href, "intention", CHANGER_DE_MOBILE_PARAM))
            } else return window.history.replaceState({}, null, updateUrlWithParam(window.location.href, "intention", this.intentionParam))
        },
        updateUrlWithReviewsSort() {
            if (this.reviewDateSortParam != null && this.reviewDateSortParam !== "") {
                return window.history.replaceState({}, null, updateUrlWithParam(window.location.href, "reviewSort", this.reviewDateSortParam))
            }
        },
        getMoreDeviceReviews: function () {
            if (this.displayRatings) {
                this.deviceDetailsReviews.loading = true;
                this.fetchDeviceReviews().then(result => {
                    this.deviceDetailsReviews.cursor = result.nextCursor;
                    this.deviceDetailsReviews.reviews = this.deviceDetailsReviews.reviews.concat(result.reviews);
                    this.deviceDetailsReviews.loading = false;
                });
            }
        },
        updateDeviceDetailsReviews: function () {
            if (this.displayRatings) {
                this.deviceDetailsReviews.loading = true;
                this.fetchDeviceReviews().then(result => {
                    this.deviceDetailsReviews.cursor = result.nextCursor;
                    this.deviceDetailsReviews.reviews = result.reviews;
                    this.deviceDetailsReviews.loading = false;
                });
            }
        },
        loadDeviceReviews: function () {
            if (this.displayRatings) {
                this.selectedRatingsFilter = [];
                this.deviceDetailsReviews.loading = true;
                this.deviceFilterReviews.loading = true;
                this.fetchDeviceReviews().then(result => {
                    this.deviceDetailsReviews.cursor = result.nextCursor;
                    this.deviceDetailsReviews.reviews = result.reviews;
                    this.deviceFilterReviews.ratingsFilter = result.ratingsFilter;
                    this.updateSummaries(result);
                    this.deviceDetailsReviews.loading = false;
                    this.deviceFilterReviews.loading = false;
                });
            }
        },
        updateSummaries(result) {
            const summariesByEan = result?.summaries || {}
            const updatedAllVersions = (this.allVersions).map((version) => {
                const ean = version?.ean;
                const summary = ean ? summariesByEan[ean] : undefined;

                if (summary) {
                    // Ajoute/écrase une propriété "summary" sur la version
                    return {
                        ...version,
                        summary
                    };
                } else return version;
            });

            // Remplace la collection (pour assurer la réactivité)
            this.allVersions = updatedAllVersions;

            // Pareil, on écrase la currentVersion si on récupère le résumé sur la décli liée à l'url
            const currentSeoId = this.currentVersion?.seoId;
            if (currentSeoId) {
                const refreshedCurrent = updatedAllVersions.find(it => it?.seoId === currentSeoId);
                if (refreshedCurrent) this.currentVersion = refreshedCurrent;
            }
        },
        fetchDeviceReviews: function () {
            let url = "/api/mobile/device-reviews";
            return new Promise((resolve) => {
                $.ajax({
                    method: "GET",
                    data: {
                        "shop": this.shop,
                        "universe": 'MOBILE',
                        "intention": 'terminal-seul',
                        "eans": this.allVersions.map(it => it.ean),
                        "first-party": this.currentVersion.firstParty,
                        "cursor": this.deviceDetailsReviews.cursor,
                        "ratings-input-filter": this.selectedRatingsFilter.toString(),
                        "reviewSort": this.reviewDateSortParam,
                    },
                    xhrFields: {
                        withCredentials: true
                    },
                    traditional: true, // pour éviter que jquery rename le paramètre eans -> eans[]
                    crossDomain: true,
                    url: url,
                    contentType: "application/json",
                    dataType: "json"
                }).done(result => {
                    resolve(result)
                }).fail((error) => {
                    resolve(error.responseJSON)
                });
            });
        },
        updateRatingsFilterHandler(selectedRatingsFilter) {
            this.selectedRatingsFilter = selectedRatingsFilter;
            this.deviceDetailsReviews.cursor = null;
            this.updateDeviceDetailsReviews()
        },
        reviewDateSortHandler(sortValue) {
            this.reviewDateSortParam = sortValue;
            this.deviceDetailsReviews.cursor = null;
            this.updateDeviceDetailsReviews();
            this.updateUrlWithReviewsSort();
        },
        scrollToPageMenuAnchor: function (id) {
            //this.activeTab = id;
            const element = document.getElementById(id);
            if (element) {
                // on ajoute 2px pour trigger l'événement de changement d'onglet et que le bon soit indiqué comme actif
                scrollToElement(element, {dx: 0, dy: -this.pageMenuOffsetHeight + 2});
            }
        },
        getGradePopinAction() {
            const grade = this.currentVersion.grade;
            return grade ? `voir_${toTmsFormat(grade.label)}` : "";
        },
        openAuthenticationAfterDisponibilities() {
            const allPopin = $('#allDisponibilitiesPopIn');
            allPopin.off('hidden.bs.modal')           // ① évite les doublons de listeners
                .one('hidden.bs.modal', () => {
                    $('#authenticationPopIn').modal('show'); // ③ s'ouvre APRÈS fermeture complète
                });
            allPopin.modal('hide');                   // ② déclenche la fermeture
        },
        handleCtaClick() {
            const ctaRef = this.$refs['add-device-cta']
            if(ctaRef.href) {
                this.$refs['add-device-cta'].startAnimation()
            }
        },
    }
};
</script>

<style scoped lang="scss">
@import 'ob1_common';
@import 'ob1_variables';

.header {
    @media (min-width: #{map-get($grid-breakpoints, md)}) {
        align-items: flex-end;
    }
}

.info-device-width {
    width: 100%;
    @media (min-width: #{map-get($grid-breakpoints, md)}) {
        width: 46%;
    }
}

.border-right-md {
    @media (min-width: #{map-get($grid-breakpoints, md)}) {
        border-right: px-to-rem(30) solid;
    }
}

.sticky {
    position: sticky;
    top: 0;
    z-index: 3;
}

.breadcrumb-full ::v-deep .breadcrumb {
    max-width: 100%;
}
</style>
